import _extends from "@babel/runtime/helpers/extends";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _min from "lodash/min";
import _max from "lodash/max";
import _isNil from "lodash/isNil";
/* eslint-disable no-nested-ternary */
function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); keys.push.apply(keys, symbols); } return keys; }
function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }
import { Icon } from '@datacamp/le-shared-components';
import * as React from 'react';
import ReactHoverObserver from 'react-hover-observer';
import useSetWidthOnDrop from '../../lib/hooks/useSetWidthOnDrop';
import StyledItem from '../styled/StyledItem';
import { ItemContent } from './ItemContent';
import { jsx as ___EmotionJSX } from "@emotion/react";
const OFFSET_MARGIN = 48;
const shouldHighlightCode = language => !_isNil(language);
const getRootStyle = function (isDragging) {
  let draggableStyle = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : {};
  return _objectSpread(_objectSpread({
    margin: "0 0 8px 0"
  }, draggableStyle), {}, {
    transform: "".concat(draggableStyle.transform || '', " ").concat(isDragging ? 'rotate(-1deg)' : '')
  });
};
const getBarsIconStyle = (isDragging, highlightCode) => {
  if (highlightCode) {
    return 0.3;
  }
  return isDragging ? 1 : 0.3;
};
const Item = _ref => {
  let {
    item,
    languageOfTarget,
    maxOffset,
    providedDraggable,
    setItemOffset,
    snapshotDraggable
  } = _ref;
  const offset = item.offset || 0;
  const itemRef = React.useRef(null);
  useSetWidthOnDrop(snapshotDraggable.isDropAnimating, itemRef, offset * OFFSET_MARGIN);
  React.useEffect(() => {
    if (offset > maxOffset) {
      setItemOffset(item.id, 0);
    }
  }, [item.id, maxOffset, offset, setItemOffset]);
  const updateItemOffset = React.useCallback(newOffset => setItemOffset(item.id, newOffset), [item.id, setItemOffset]);
  const moveItemToLeft = React.useCallback(() => updateItemOffset(_max([offset - 1, 0])), [offset, updateItemOffset]);
  const moveItemToRight = React.useCallback(() => updateItemOffset(_min([offset + 1, maxOffset])), [offset, updateItemOffset, maxOffset]);
  const language = _isNil(item.language) ? languageOfTarget : item.language;
  const highlightCode = shouldHighlightCode(language);
  const barIconOpacity = getBarsIconStyle(snapshotDraggable.isDragging, highlightCode);
  return ___EmotionJSX(ReactHoverObserver, null, _ref2 => {
    let {
      isHovering
    } = _ref2;
    return ___EmotionJSX("div", _extends({
      ref: providedDraggable.innerRef
    }, providedDraggable.draggableProps, providedDraggable.dragHandleProps, {
      "data-cy": "draggable-item",
      style: getRootStyle(snapshotDraggable.isDragging, providedDraggable.draggableProps.style)
    }), ___EmotionJSX(StyledItem, {
      className: "dc-u-fx dc-u-fx-fdr dc-u-fx-jcc dc-u-fx-aic dc-u-brad-all dc-u-pos-relative dc-u-of-hidden",
      highlight: item.highlight,
      highlightCode: highlightCode,
      isDragging: snapshotDraggable.isDragging,
      offset: offset,
      ref: itemRef
    }, item.status === 'INCORRECT' && ___EmotionJSX("div", {
      // Hacky css to show a thin red line on top of content without moving it
      style: {
        background: '#fe5c5c',
        borderBottomLeftRadius: 4,
        borderTopLeftRadius: 4,
        height: 'calc(100% + 2px)',
        left: -1,
        position: 'absolute',
        top: -1,
        width: 5
      }
    }), ___EmotionJSX(ItemContent, {
      highlightCode: highlightCode,
      isDragging: snapshotDraggable.isDragging,
      item: item,
      language: language
    }), ___EmotionJSX(Icon, {
      color: "white",
      extraClass: "dc-u-mh-4",
      name: "arrow_left",
      onClick: moveItemToLeft,
      size: 15,
      style: {
        cursor: 'pointer',
        display: maxOffset > 0 && !snapshotDraggable.isDragging ? 'inherit' : 'none',
        flex: '0 0 auto',
        height: 40
      }
    }), ___EmotionJSX(Icon, {
      color: "white",
      name: "arrow_right",
      onClick: moveItemToRight,
      size: 15,
      style: {
        cursor: 'pointer',
        display: maxOffset > 0 && !snapshotDraggable.isDragging ? 'inherit' : 'none',
        flex: '0 0 auto',
        height: 40,
        marginRight: '-4px'
      }
    }), item.status === 'CORRECT' ? ___EmotionJSX(Icon, {
      color: "green",
      name: "checkmark_circle",
      size: 16,
      style: {
        flex: '0 0 auto',
        margin: '14px 14px 14px 6px',
        position: 'absolute',
        right: '-10px',
        top: '-10px'
      }
    }) : item.status === 'INCORRECT' ? ___EmotionJSX(Icon, {
      color: "red",
      name: "error",
      size: 16,
      style: {
        flex: '0 0 auto',
        margin: '14px 14px 14px 6px',
        position: 'absolute',
        right: '-10px',
        top: '-10px'
      }
    }) : ___EmotionJSX(Icon, {
      color: "navy",
      name: "bars",
      size: 12,
      style: {
        flex: '0 0 auto',
        margin: '12px 18px',
        opacity: barIconOpacity,
        position: 'absolute',
        right: '-12px',
        top: '-7px'
      }
    })));
  });
};
export default Item;